import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Droplet, Zap, Flame, Paintbrush, KeyRound, Wind,
  Wrench, Leaf, Home, Banknote, Landmark, Smartphone,
  CreditCard, Plus, Check, ChevronRight, Loader2,
} from 'lucide-react';
import { trackProfessionalProfileCompleted } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { key: 'plomeria', label: 'Plomería', Icon: Droplet },
  { key: 'electricidad', label: 'Electricidad', Icon: Zap },
  { key: 'gasista', label: 'Gasista', Icon: Flame },
  { key: 'pintura', label: 'Pintura', Icon: Paintbrush },
  { key: 'cerrajeria', label: 'Cerrajería', Icon: KeyRound },
  { key: 'aire_acondicionado', label: 'Aire acondicionado', Icon: Wind },
  { key: 'arreglos_generales', label: 'Arreglos Generales', Icon: Wrench },
  { key: 'jardineria', label: 'Jardinería', Icon: Leaf },
  { key: 'techista', label: 'Techista', Icon: Home },
];

const PAYMENT_METHODS = [
  { key: 'efectivo', label: 'Efectivo', Icon: Banknote },
  { key: 'transferencia', label: 'Transferencia bancaria', Icon: Landmark },
  { key: 'mercado_pago', label: 'Mercado Pago', Icon: Smartphone },
  { key: 'tarjeta', label: 'Tarjeta', Icon: CreditCard },
  { key: 'otro', label: 'Otro', Icon: Plus },
];

export default function ProviderProfileSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');

  const [services, setServices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Guard: sin sesión de proveedor, volver al login
  useEffect(() => {
    if (!localStorage.getItem('provider_token')) {
      navigate('/provider-auth', { replace: true });
    }
  }, [navigate]);

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const nextUrl = ticketId ? `/providers-pro?ticketId=${ticketId}` : '/providers-pro';

  const handleContinue = async () => {
    if (services.length === 0) {
      toast.error('Seleccioná al menos un servicio para continuar.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('provider_token');
      await axios.post(
        `${API}/providers/profile`,
        { services, payment_methods: payments },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      trackProfessionalProfileCompleted();
      navigate(nextUrl, { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No pudimos guardar tu perfil. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="provider-profile-setup">
      {/* Encabezado naranja con progreso */}
      <header className="px-5 pt-6 pb-8 rounded-b-3xl" style={{ backgroundColor: 'var(--primary-strong)' }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-white/90 mb-2">Paso 2 de 2</p>
          <div className="h-1.5 w-full rounded-full bg-white/30 mb-6">
            <div className="h-1.5 w-full rounded-full bg-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-white leading-tight mb-2">
            Completá tu perfil profesional
          </h1>
          <p className="text-sm text-white/90 leading-relaxed">
            Cuanto más completo esté tu perfil, mejor podremos mostrarte oportunidades que se adapten a vos.
          </p>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Servicios */}
          <section>
            <h2 className="text-lg font-bold text-gray-900">¿Qué servicios ofrecés?</h2>
            <p className="text-sm text-gray-500 mt-1">Seleccioná todos los servicios que realizás.</p>
            <p className="text-sm font-semibold text-orange-500 mt-2" data-testid="services-count">
              {services.length} {services.length === 1 ? 'servicio seleccionado' : 'servicios seleccionados'}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
              {SERVICES.map(({ key, label, Icon }) => {
                const selected = services.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`service-${key}`}
                    onClick={() => toggle(services, setServices, key)}
                    className={`relative flex items-center gap-3 rounded-2xl border p-3 text-left transition-all ${
                      selected ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      selected ? 'text-white' : 'bg-gray-100 text-gray-400'
                    }`} style={selected ? { backgroundColor: 'var(--primary-strong)' } : undefined}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className={`text-sm font-semibold leading-tight ${
                      selected ? 'text-orange-600' : 'text-gray-800'
                    }`}>
                      {label}
                    </span>
                    {selected && (
                      <Check className="w-4 h-4 text-orange-500 absolute top-2.5 right-2.5" strokeWidth={3} />
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Medios de pago */}
          <section>
            <h2 className="text-lg font-bold text-gray-900">¿Cómo cobrás tus trabajos?</h2>
            <p className="text-sm text-gray-500 mt-1">Indicá qué medios de pago aceptás.</p>

            <div className="flex flex-wrap gap-2.5 mt-4">
              {PAYMENT_METHODS.map(({ key, label, Icon }) => {
                const selected = payments.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`payment-${key}`}
                    onClick={() => toggle(payments, setPayments, key)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-all ${
                      selected
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {selected && <Check className="w-4 h-4" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Continuar */}
          <div className="flex md:justify-end">
            <button
              type="button"
              data-testid="profile-continue-btn"
              onClick={handleContinue}
              disabled={submitting || services.length === 0}
              className="w-full md:w-auto md:px-16 py-3.5 rounded-2xl font-semibold text-white text-base transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--primary-strong)' }}
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Continuar
              {!submitting && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
